import { motion } from "motion/react";
import { useState } from "react";
import Toast from "./Toast";

interface Notification {
  id: string;
  type: "test-due" | "reminder" | "quality-alert";
  title: string;
  message: string;
  time: string;
  isRead: boolean;
  action?: {
    label: string;
    onClick: () => void;
  };
}

interface NotificationsViewProps {
  notifications: Notification[];
  onMarkAsRead: (id: string) => void;
  onDelete: (id: string) => void;
  unreadCount: number;
}

export default function NotificationsView({
  notifications,
  onMarkAsRead,
  onDelete,
  unreadCount,
}: NotificationsViewProps) {
  const [showToast, setShowToast] = useState<{ message: string; type: "success" | "info" } | null>(null);

  const typeConfig = {
    "test-due": { color: "#F59E0B" },
    "reminder": { color: "#3B82F6" },
    "quality-alert": { color: "#DC2626" },
  };

  const handleMarkAllAsRead = () => {
    notifications.forEach((n) => {
      if (!n.isRead) {
        onMarkAsRead(n.id);
      }
    });
    setShowToast({ message: "All notifications marked as read", type: "success" });
  };

  const handleClearAll = () => {
    notifications.forEach((n) => onDelete(n.id));
    setShowToast({ message: "All notifications cleared", type: "success" });
  };

  return (
    <div className="pb-32 bg-white min-h-screen">
      {/* Header */}
      <div className="px-5 pt-6 pb-4 flex items-start justify-between">
        <h1
          className="text-[#0A0F1C]"
          style={{
            fontSize: "18px",
            letterSpacing: "-0.01em",
            fontWeight: "600",
          }}
        >
          Notifications
        </h1>
        {unreadCount > 0 && (
          <div
            className="w-5 h-5 rounded-full flex items-center justify-center"
            style={{
              backgroundColor: "#DC2626",
              fontSize: "11px",
              fontWeight: "700",
              color: "white",
            }}
          >
            {unreadCount}
          </div>
        )}
      </div>

      {/* Notification List */}
      <div className="px-5">
        {notifications.map((notification, index) => {
          const config = typeConfig[notification.type];
          return (
            <motion.button
              key={notification.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05, duration: 0.3 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => onMarkAsRead(notification.id)}
              className="w-full text-left py-4 border-b border-[#F3F4F6] flex items-start gap-3"
              style={{
                opacity: notification.isRead ? 0.5 : 1,
              }}
            >
              {/* Colored Dot Indicator */}
              <div
                className="w-2 h-2 rounded-full mt-1.5 flex-shrink-0"
                style={{ backgroundColor: config.color }}
              />

              {/* Content */}
              <div className="flex-1 min-w-0">
                <div
                  className="text-[#0A0F1C] mb-1"
                  style={{
                    fontSize: "14px",
                    fontWeight: "500",
                    lineHeight: "1.4",
                  }}
                >
                  {notification.title} - {notification.message}
                </div>
                
                {/* Time with clock icon */}
                <div className="flex items-center gap-1 text-[#717182]">
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <circle cx="6" cy="6" r="5" stroke="currentColor" strokeWidth="1"/>
                    <path d="M6 3V6L8 7" stroke="currentColor" strokeWidth="1" strokeLinecap="round"/>
                  </svg>
                  <span style={{ fontSize: "12px" }}>
                    {notification.time}
                  </span>
                </div>
              </div>
            </motion.button>
          );
        })}
      </div>

      {/* Close Button at Bottom */}
      {notifications.length > 0 && (
        <div className="px-5 pt-6 flex justify-center">
          <motion.button
            whileTap={{ scale: 0.95 }}
            onClick={handleClearAll}
            className="text-[#DC2626]"
            style={{ fontSize: "14px", fontWeight: "600" }}
          >
            Close
          </motion.button>
        </div>
      )}

      {showToast && (
        <Toast
          message={showToast.message}
          type={showToast.type}
          onClose={() => setShowToast(null)}
        />
      )}
    </div>
  );
}